/*
 * Widget assistenza flottante "Ti serve aiuto?" — logica
 * ------------------------------------------------------------
 * Custom element <help-widget>:
 *  - apre/chiude il pannello dei canali di contatto (attributo [open]);
 *  - chiude con Esc, cliccando fuori o scegliendo un'opzione;
 *  - gestisce l'opzione "Chat" che apre l'assistente IA TextYess
 *    (il click è intercettato da TextYess tramite trigger_selector);
 *  - si nasconde quando il footer entra in viewport o quando è
 *    visibile la barra sticky "aggiungi al carrello".
 */
(function () {
  if (customElements.get('help-widget')) return;

  const AI_TAG = 'textyess-ai-widget';

  class HelpWidget extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-help-toggle]');
      this.panel = this.querySelector('[data-help-panel]');

      if (!this.toggle || !this.panel) return;

      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPanelClick = this.onPanelClick.bind(this);

      this.toggle.addEventListener('click', this.onToggle);
      this.panel.addEventListener('click', this.onPanelClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      this.hiddenByFooter = false;
      this.hiddenBySticky = false;

      this.setupAi();
      this.watchFooter();
      this.watchStickyCart();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      if (this.footerObserver) this.footerObserver.disconnect();
      if (this.stickyObserver) this.stickyObserver.disconnect();
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    onToggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.setAttribute('open', '');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.removeAttribute('aria-hidden');
      // Porta il focus sulla prima opzione visibile per la navigazione da tastiera
      const first = this.panel.querySelector('.help-option-item:not([hidden]) .help-option');
      if (first) first.focus({ preventScroll: true });
    }

    close(restoreFocus) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      if (restoreFocus) this.toggle.focus({ preventScroll: true });
    }

    // Scegliendo un'opzione il pannello si chiude (link e chat IA)
    onPanelClick(event) {
      const option = event.target.closest('.help-option');
      if (!option) return;
      if (option.hasAttribute('data-help-ai')) this.onAiClick(event);
      this.close();
    }

    /*
     * Assistente IA: TextYess si aggancia da solo ai click su [data-help-ai]
     * (trigger_selector), ma solo dopo essere stato caricato. Se l'utente
     * clicca prima, si attende che il componente sia pronto e si ripete il click.
     */
    setupAi() {
      this.aiItem = this.querySelector('[data-help-ai-item]');
      this.aiButton = this.querySelector('[data-help-ai]');
      if (!this.aiItem) return;

      // Senza l'elemento TextYess in pagina l'opzione non avrebbe effetto
      if (!document.querySelector(AI_TAG)) {
        this.aiItem.hidden = true;
        return;
      }

      this.aiReady = false;
      customElements
        .whenDefined(AI_TAG)
        .then(() => {
          const el = document.querySelector(AI_TAG);
          return el && typeof el.componentOnReady === 'function' ? el.componentOnReady() : null;
        })
        .then(() => {
          this.aiReady = true;
          if (this.aiPending) {
            this.aiPending = false;
            this.aiButton.click();
          }
        });
    }

    onAiClick() {
      if (!this.aiReady) this.aiPending = true;
    }

    // Applica lo stato nascosto combinando le due condizioni
    syncHidden() {
      const hidden = this.hiddenByFooter || this.hiddenBySticky;
      this.classList.toggle('help-hidden', hidden);
      if (hidden) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close(true);
    }

    onDocumentClick(event) {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }

    // Nasconde il widget quando il footer è visibile
    watchFooter() {
      const footer = document.getElementById('footer');
      if (!footer || !('IntersectionObserver' in window)) return;
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          this.hiddenByFooter = entries.some((entry) => entry.isIntersecting);
          this.syncHidden();
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0 }
      );
      this.footerObserver.observe(footer);
    }

    // Nasconde il widget quando la barra sticky "aggiungi al carrello" è visibile
    watchStickyCart() {
      const sticky = document.querySelector('product-add-to-cart-sticky');
      if (!sticky) return;
      const sync = () => {
        this.hiddenBySticky = sticky.classList.contains('sticky--visible');
        this.syncHidden();
      };
      this.stickyObserver = new MutationObserver(sync);
      this.stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['class'] });
      sync();
    }
  }

  customElements.define('help-widget', HelpWidget);
})();
